import { useEffect, useMemo, useState } from "react";
import { media } from "src/theme";

export interface IRenderOn {
  xs: boolean;
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
  smOrDown: boolean;
  smOrUp: boolean;
  mdOrDown: boolean;
  mdOrUp: boolean;
  lgOrDown: boolean;
  lgOrUp: boolean;
}

/**
 * Returns a boolean object to detect the width of the screen
 * We can say approximately that:
 * xs is mobile
 * sm is phablet
 * md is tablet
 * lg is laptop
 * xl is desktop
 * Ranges can also be used :
 * smOrDown, smOrUp, mdOrDown, mdOrUp, lgOrDown, lgOrUp
 */
export const useRenderOn = (): IRenderOn => {
  const [width, setWidth] = useState(window.innerWidth);

  const updateWidth = (): void => setWidth(window.innerWidth);

  useEffect(() => {
    window.addEventListener("resize", updateWidth);

    return (): void => window.removeEventListener("resize", updateWidth);
  }, []);

  const memo = useMemo(() => {
    const xs = width < media.size.mobile;
    const sm = width >= media.size.mobile && width < media.size.tablet;
    const md = width >= media.size.tablet && width < media.size.laptop;
    const lg = width >= media.size.laptop && width < media.size.desktop;
    const xl = width >= media.size.desktop;

    const ret = {
      xs,
      sm,
      md,
      lg,
      xl,

      smOrDown: xs || sm,
      smOrUp: sm || md || lg || xl,

      mdOrDown: xs || sm || md,
      mdOrUp: md || lg || xl,

      lgOrDown: xs || sm || md || lg,
      lgOrUp: lg || xl,
    };

    return ret;
  }, [width]);

  return memo;
};
